import { ComponentType, isValidElement, lazy, LazyExoticComponent } from 'react'
import { matchRoutes, RouteObject } from 'react-router-dom'
import { voidHandler } from './helpers'
import { runWhenIdleAfterLoad } from './runWhenIdle'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- route components take any props
type PageLoader = () => Promise<{ default: ComponentType<any> }>

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- route components take any props
export type PreloadableComponent = LazyExoticComponent<ComponentType<any>> & {
  preload: PageLoader
}

/** `lazy()` that can also be fetched ahead of a navigation. */
export const lazyPage = (loader: PageLoader): PreloadableComponent => {
  let pending: ReturnType<PageLoader> | null = null
  const load = () => {
    pending ??= loader().catch((error: unknown) => {
      pending = null
      throw error
    })
    return pending
  }
  // React.lazy keeps a rejection forever, and the one it picked up may be a preload's (whose
  // failure skips chunk recovery), so the render retries once with an import of its own.
  return Object.assign(
    lazy(() => load().catch(load)),
    { preload: load },
  )
}

const preloadOf = (value: unknown): PageLoader | undefined =>
  (value as Partial<PreloadableComponent> | undefined)?.preload

let speculativeImports = 0

/**
 * A preload is a guess, so a chunk it fails to load (offline, or gone after a deploy) must not
 * trigger chunk recovery's page reload — that would interrupt whatever the user is doing.
 */
export const isSpeculativeImportInFlight = (): boolean => speculativeImports > 0

/** Route elements are `<PrivateRoute component={Page} />` or `<Page />`; either carries the page. */
const preloadElement = (element: unknown): void => {
  if (!isValidElement(element)) {
    return
  }
  const props = element.props as { component?: unknown }
  const preload = preloadOf(props.component) ?? preloadOf(element.type)
  if (!preload) {
    return
  }
  speculativeImports += 1
  void preload()
    .catch(voidHandler)
    .finally(() => {
      speculativeImports -= 1
    })
}

/** Starts fetching and evaluating the page chunk(s) a path renders, so the tap lands instantly. */
export const preloadRoute = (routes: RouteObject[], pathname: string): void => {
  matchRoutes(routes, pathname)?.forEach(({ route }) => preloadElement(route.element))
}

const NAV_PRELOAD_SELECTOR = '[data-preload-routes] a[href]'
const PRELOAD_EVENTS = ['pointerover', 'touchstart', 'focusin'] as const

const linkPathname = (target: EventTarget | null): string | null => {
  const anchor = target instanceof Element ? target.closest('a[href]') : null
  if (!(anchor instanceof HTMLAnchorElement) || anchor.origin !== window.location.origin) {
    return null
  }
  return anchor.pathname
}

/**
 * Prefetches a page as soon as a link to it is hovered, touched or focused, and the nav
 * menus' destinations once the app is idle — page chunks are otherwise fetched only after
 * the tap, which the router spends holding the previous page on screen.
 */
export const registerRoutePreloading = (routes: RouteObject[]): void => {
  const handleIntent = (event: Event) => {
    const pathname = linkPathname(event.target)
    if (pathname) {
      preloadRoute(routes, pathname)
    }
  }
  PRELOAD_EVENTS.forEach((type) =>
    document.addEventListener(type, handleIntent, { capture: true, passive: true }),
  )

  const preloadNavDestinations = () =>
    document.querySelectorAll<HTMLAnchorElement>(NAV_PRELOAD_SELECTOR).forEach((anchor) => {
      if (anchor.origin === window.location.origin) {
        preloadRoute(routes, anchor.pathname)
      }
    })
  runWhenIdleAfterLoad(preloadNavDestinations)
}
